import { useState, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { Badge } from '@/components/ui/Badge';
import { JOB_ROLE_OPTIONS } from '@/lib/constants';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface StaffSearchResult {
  id: string;
  name: string;
  email: string;
  roleName: string | null;
  jobRole: string | null;
  department: string | null;
  status: string;
}

export interface StaffSearchModalProps {
  /** Called when a staff member is selected. */
  onSelect: (staff: StaffSearchResult) => void;
  /** Called when the modal is closed without selection. */
  onClose: () => void;
  /** Modal title override. */
  title?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Shared modal for searching and selecting a staff member (user).
 *
 * Mirrors ClientSearchModal, but for internal users. Supports type-ahead search
 * (name / email / department) plus user-relative property filters: status and
 * job role. Used to attach an account/transaction to a specific staff member
 * (e.g. the billing "Partner").
 */
export function StaffSearchModal({
  onSelect,
  onClose,
  title = 'Search & Select Staff',
}: StaffSearchModalProps) {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('active');
  const [jobRole, setJobRole] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 15;

  // Reset page when filters change.
  useEffect(() => {
    setPage(1);
  }, [search, status, jobRole]);

  const { data, isLoading } = useQuery({
    queryKey: ['staff-search-modal', search, status, jobRole, page],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      if (search) params.set('search', search);
      if (status) params.set('status', status);
      if (jobRole) params.set('job_role', jobRole);

      const res = await apiClient.get<{
        success: boolean;
        data: Record<string, unknown>[];
        meta?: { totalCount?: number };
      }>(`/users?${params.toString()}`);

      const items: StaffSearchResult[] = (res.data.data ?? []).map((u) => ({
        id: u.id as string,
        name: (u.name || '') as string,
        email: (u.email || '') as string,
        roleName: (u.role_name || u.roleName || null) as string | null,
        jobRole: (u.job_role || u.jobRole || null) as string | null,
        department: (u.department || null) as string | null,
        status: (u.status || 'active') as string,
      }));

      return { items, total: res.data.meta?.totalCount ?? items.length };
    },
    staleTime: 30_000,
  });

  const handleClick = useCallback(
    (staff: StaffSearchResult) => {
      onSelect(staff);
    },
    [onSelect],
  );

  const staff = data?.items ?? [];
  const total = data?.total ?? 0;

  const jobRoleLabel = (value: string | null): string => {
    if (!value) return '';
    return JOB_ROLE_OPTIONS.find((o) => o.value === value.toLowerCase())?.label || value;
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="staff-search-modal-title"
    >
      <div className="flex h-[80vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-2xl dark:bg-gray-900">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <h2
            id="staff-search-modal-title"
            className="text-lg font-semibold text-gray-900 dark:text-gray-100"
          >
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            aria-label="Close"
          >
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-1 flex-col overflow-hidden">
          {/* Search + Filters */}
          <div className="border-b border-gray-200 bg-gray-50 px-6 py-3 dark:border-gray-700 dark:bg-gray-800/50">
            <div className="mb-3">
              <div className="relative">
                <svg
                  className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Type to search by name, email, or department..."
                  className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                  autoFocus
                />
              </div>
            </div>

            {/* Filter row */}
            <div className="flex flex-wrap gap-2">
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                aria-label="Filter by status"
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="invited">Invited</option>
                <option value="locked">Locked</option>
              </select>

              <select
                value={jobRole}
                onChange={(e) => setJobRole(e.target.value)}
                className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                aria-label="Filter by job role"
              >
                <option value="">All Roles</option>
                {JOB_ROLE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Results */}
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
                <span className="ml-2 text-sm text-gray-500">Searching staff...</span>
              </div>
            ) : staff.length === 0 ? (
              <p className="py-12 text-center text-sm text-gray-500">
                No staff found. Try adjusting your search or filters.
              </p>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                {staff.map((member) => (
                  <li key={member.id}>
                    <button
                      type="button"
                      onClick={() => handleClick(member)}
                      className="flex w-full items-start gap-4 px-6 py-3.5 text-left transition-colors hover:bg-brand-50 dark:hover:bg-brand-900/10"
                    >
                      {/* Avatar */}
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-100 text-sm font-medium text-brand-700 dark:bg-brand-900 dark:text-brand-300">
                        {(member.name || member.email || '?').charAt(0).toUpperCase()}
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="truncate font-medium text-gray-900 dark:text-gray-100">
                            {member.name || member.email}
                          </span>
                          {member.jobRole && (
                            <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                              {jobRoleLabel(member.jobRole)}
                            </span>
                          )}
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-500 dark:text-gray-400">
                          {member.email && <span>{member.email}</span>}
                          {member.roleName && <span>Role: {member.roleName}</span>}
                          {member.department && <span>Dept: {member.department}</span>}
                        </div>
                      </div>

                      <div className="flex shrink-0 items-center gap-2">
                        <Badge
                          variant={member.status === 'active' ? 'success' : 'default'}
                          size="sm"
                        >
                          {member.status.charAt(0).toUpperCase() + member.status.slice(1)}
                        </Badge>
                        <svg
                          className="h-4 w-4 text-gray-400"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Pagination */}
          {total > pageSize && (
            <div className="flex items-center justify-between border-t border-gray-200 px-6 py-3 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400">
              <span>{total} staff found</span>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="rounded border border-gray-300 px-2 py-1 hover:bg-gray-50 disabled:opacity-40 dark:border-gray-600 dark:hover:bg-gray-800"
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={page * pageSize >= total}
                  onClick={() => setPage((p) => p + 1)}
                  className="rounded border border-gray-300 px-2 py-1 hover:bg-gray-50 disabled:opacity-40 dark:border-gray-600 dark:hover:bg-gray-800"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
